// Alerts Phase 2 — pipes server-side alarm events into the Android
// WebView's native AlarmManager via window.VultureAlarms. Without this
// hook, alarms set from chat on one device only ring on THAT device:
// the server records them, but native AlarmManager only knows what was
// scheduled directly through the bridge.
//
// What this hook does:
//   1. On mount, calls api.alarmsList() and re-programs every active
//      alarm into VultureAlarms (idempotent — same id overwrites). This
//      catches the case where the phone was offline / asleep while an
//      alarm was set on the laptop.
//   2. Subscribes to the SSE channel for alarm:schedule + alarm:cancel
//      so new alarms set anywhere fan out to the native scheduler
//      within a couple of seconds.
//
// Web-only browsers (no `window.VultureAlarms`) are a complete no-op —
// they fall back to browser Notification API later if we want, but the
// MVP household use case is "set on laptop, fire on phone."

import { useEffect } from "preact/hooks";
import { api } from "./rpc";
import { sse } from "./sse";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface NativeAlarmsBridge {
  set(json: string): string;
  list(): string;
  cancel(id: string): string;
}

function nativeBridge(): NativeAlarmsBridge | null {
  const w = typeof window !== "undefined" ? (window as any) : null;
  const b = w?.VultureAlarms;
  return b && typeof b.set === "function" ? (b as NativeAlarmsBridge) : null;
}

// Server's `alarm:schedule` event ships the full alarm row; the native
// bridge only cares about a subset. Map between shapes here so the
// bridge contract stays narrow (less surface to break across native
// versions).
function programNative(bridge: NativeAlarmsBridge, alarm: {
  id: string; name: string; body?: string; fireAt: number; atHHMM?: string | null;
}): void {
  try {
    bridge.set(JSON.stringify({
      id: alarm.id,
      name: alarm.name,
      body: alarm.body || "",
      fireAt: alarm.fireAt,
      atHHMM: alarm.atHHMM || undefined,
    }));
  } catch (err: any) {
    console.warn(`[alarms-bridge] set failed for ${alarm.id}:`, err?.message || err);
  }
}

function cancelNative(bridge: NativeAlarmsBridge, id: string): void {
  try { bridge.cancel(id); }
  catch (err: any) { console.warn(`[alarms-bridge] cancel failed for ${id}:`, err?.message || err); }
}

// Compute when this alarm should actually fire next on the OS scheduler.
//
// The server stores the alarm's ORIGINAL fireAt and never updates it
// (post-fire bookkeeping happens on-device — see AlarmReceiver's daily
// reschedule). That means boot sync and any `alarm:schedule` SSE event
// will hand us a fireAt that's in the past for any daily alarm whose
// original schedule time has elapsed, plus any one-shot whose user-set
// time is now history. Handing a past timestamp to
// `AlarmManager.setExactAndAllowWhileIdle` causes Android to fire the
// alarm IMMEDIATELY — which manifests as alarms going off at random
// times, every app open, until the user disables alarms entirely.
//
// Returns null when there's nothing to schedule (a one-shot whose time
// has come and gone — we don't fire missed alarms retroactively).
function computeNextFire(alarm: { fireAt: number; atHHMM?: string | null }): number | null {
  const now = Date.now();
  if (Number.isFinite(alarm.fireAt) && alarm.fireAt > now) return alarm.fireAt;
  const hhmm = alarm.atHHMM;
  if (!hhmm) return null; // one-shot in the past — treat as already-missed
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  const next = new Date();
  next.setHours(h, min, 0, 0);
  if (next.getTime() <= now) next.setDate(next.getDate() + 1);
  return next.getTime();
}

export function useAlarmsBridge(): void {
  useEffect(() => {
    const bridge = nativeBridge();
    if (!bridge) return; // regular browser — no-op
    let cancelled = false;

    // Boot-time sync: re-arm every server-known active alarm against
    // the native scheduler. The server's `fireAt` may be in the past
    // (it doesn't update on fire), so route every alarm through
    // computeNextFire — that filters out missed one-shots and rolls
    // daily alarms forward to today/tomorrow.
    //
    // Reconciliation: any alarm currently scheduled LOCALLY (per
    // VultureAlarms.list()) that's NOT in the server's set gets
    // cancelled. This catches the case where an alarm:cancel SSE
    // event was missed (phone offline / app backgrounded / WebView
    // killed) — without this reconcile, the orphaned PendingIntent
    // would keep firing at the old time forever. Real-world repro:
    // husband moves a shared-calendar event on his phone, wife's
    // phone is asleep so misses the SSE; on next app open her old
    // PendingIntent still fires at the original time + the new one
    // also fires at the new time.
    api.alarmsList()
      .then((alarms) => {
        if (cancelled) return;
        const serverIds = new Set<string>();
        for (const a of alarms) {
          if (!a.enabled) continue;
          const fireAt = computeNextFire(a);
          if (fireAt === null) continue;
          programNative(bridge, { ...a, fireAt });
          serverIds.add(a.id);
        }
        // Diff against the bridge's locally-persisted set; cancel
        // anything orphaned. The bridge stores its full state in
        // SharedPreferences, so this catches drift even across app
        // re-installs that re-prime native AlarmManager from prefs
        // via BootReceiver.
        try {
          const localRaw = bridge.list();
          const parsed = JSON.parse(localRaw || "{}");
          const localAlarms = parsed?.alarms || [];
          for (const local of localAlarms) {
            const id = typeof local === "string" ? local : local?.id;
            if (!id) continue;
            if (serverIds.has(id)) continue;
            cancelNative(bridge, id);
          }
        } catch (err) {
          console.warn("[alarms-bridge] reconcile orphans failed:", (err as Error)?.message || err);
        }
      })
      .catch((err) => {
        console.warn("[alarms-bridge] boot sync failed:", err?.message || err);
      });

    // Live SSE updates. alarm:schedule fires on alarms.set (and on the
    // agent's set_alarm tool); alarm:cancel fires on alarms.cancel.
    // Both events are user-scoped (sseSendToUser) so multiple tabs /
    // devices on the same account all stay in sync.
    const unsub = sse.on((ev: any) => {
      if (ev.type === "alarm:schedule" && ev.alarm) {
        const fireAt = computeNextFire(ev.alarm);
        if (fireAt === null) return;
        programNative(bridge, { ...ev.alarm, fireAt });
        return;
      }
      if (ev.type === "alarm:cancel" && typeof ev.id === "string") {
        cancelNative(bridge, ev.id);
        return;
      }
    });
    return () => { cancelled = true; unsub(); };
  }, []);
}
